import React, { useEffect, useState } from "react";
import { Button, Grid, TextField, Typography } from "@material-ui/core";
import { OwnerClubSettings } from "api/owner/settings";
import { getBalance, getStripeDashboardLink, getStripeConnectLink } from "api/owner/stripe";
import { Club } from "api/clubs";
import { useSnackbar } from "notistack";
import i18n from "../i18n";
import DeactivateStripeDialog from "./DeactivateStripeDialog";

// prop types
interface StripeSectionProps {
    club?: Club,
    clubSettings?: OwnerClubSettings,
    readyToSetupStripe: boolean,
    onFetchUserProfile: () => void,
}

// currency formatter
const formatter = Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" });

// stripe section
const StripeSection = ({ club, clubSettings, readyToSetupStripe, onFetchUserProfile }: StripeSectionProps) => {
    // snackbar
    const { enqueueSnackbar } = useSnackbar();
    const displayError = (error: any) => enqueueSnackbar(
        `${i18n.anErrorOccurred}: ${error.description} (${error.type})`,
        { variant: "error" },
    );

    // ui state
    const [balance, setBalance] = useState(0);
    const [showDeactivateStripeDialog, setShowDeactivateStripeDialog] = useState(false);

    // fetch club settings
    const fetchBalance = async () => {
        const balances = await getBalance({ clubId: club!.id });
        setBalance(balances.available.reduce((acc, curr) => acc + curr.amountCents, 0));
    };
    useEffect(() => { if (club) fetchBalance(); }, [club]); // eslint-disable-line

    // show payouts action
    const showPayouts = async () => {
        try {
            const { link } = await getStripeDashboardLink({ clubId: club!.id });
            window.open(link);
        } catch (error) {
            displayError(error);
        }
    };

    // connect with stripe
    const connectWithStripe = async () => {
        try {
            const { link } = await getStripeConnectLink({
                clubId: club!.id,
                redirectUri: window.location.origin,
            });
            window.open(link);
        } catch (error) {
            displayError(error);
        }
    };

    // render
    return clubSettings?.stripeAccountId ? (
        <>
            <Grid item xs={4}>
                <TextField
                    disabled
                    fullWidth
                    helperText={i18n.payoutsArePaidMonthly}
                    inputProps={{ style: { color: "black" } }}
                    label={i18n.balance}
                    value={formatter.format(balance / 100)}
                    variant="outlined"
                />
            </Grid>
            <Grid item xs={12} style={{ display: "flex", justifyContent: "center" }}>
                <Button variant="contained" onClick={showPayouts}>
                    {i18n.showPayouts}
                </Button>
            </Grid>
            <Grid item xs={12} style={{ display: "flex", justifyContent: "center" }}>
                <Button variant="contained" onClick={() => setShowDeactivateStripeDialog(true)}>
                    {i18n.deactivateStripe}
                </Button>
            </Grid>
            <DeactivateStripeDialog
                club={club}
                show={showDeactivateStripeDialog}
                onClose={() => setShowDeactivateStripeDialog(false)}
                onFetchUserProfile={onFetchUserProfile}
            />
        </>
    ) : (
        <Grid item xs={12} style={{ display: "flex", justifyContent: "center" }}>
            {readyToSetupStripe ? (
                <Button variant="contained" disabled={!readyToSetupStripe} onClick={connectWithStripe}>
                    {i18n.connectWithStripe}
                </Button>
            ) : (
                <Typography variant="caption">{i18n.stripePrereq}</Typography>
            )}
        </Grid>
    );
};

export default StripeSection;
